import React, { useState } from 'react';
import { TrendingDown, AlertTriangle, Plus } from 'lucide-react';

interface Coste {
  id: string; concepto: string; obra: string; proveedor: string;
  fecha: string; importe: number; tipo: 'material' | 'mano_obra' | 'subcontrata' | 'otros';
  estado: 'pendiente' | 'pagado';
}

const DEMO: Coste[] = [
  { id: '1', concepto: 'Hormigón HA-25 m³', obra: 'Construcción Chalet Goya', proveedor: 'Hormigones SA', fecha: '05/03/2026', importe: 4200, tipo: 'material', estado: 'pagado' },
  { id: '2', concepto: 'Ferralla B-500S', obra: 'Construcción Chalet Goya', proveedor: 'Aceros Norte', fecha: '06/03/2026', importe: 3100, tipo: 'material', estado: 'pagado' },
  { id: '3', concepto: 'Electricidad subcontrata', obra: 'Reforma Oficinas', proveedor: 'Electro Servicios', fecha: '04/03/2026', importe: 6800, tipo: 'subcontrata', estado: 'pendiente' },
  { id: '4', concepto: 'Mano de obra semana 9', obra: 'Reforma Oficinas', proveedor: 'Interno', fecha: '07/03/2026', importe: 5200, tipo: 'mano_obra', estado: 'pagado' },
  { id: '5', concepto: 'Azulejos baño + cocina', obra: 'Reforma Oficinas', proveedor: 'Cerámica Ibérica', fecha: '06/03/2026', importe: 2800, tipo: 'material', estado: 'pendiente' },
  { id: '6', concepto: 'Mano de obra semana 9', obra: 'Construcción Chalet Goya', proveedor: 'Interno', fecha: '07/03/2026', importe: 7800, tipo: 'mano_obra', estado: 'pagado' },
  { id: '7', concepto: 'Carpintería exterior PVC', obra: 'Construcción Chalet Goya', proveedor: 'PVC Windows', fecha: '05/03/2026', importe: 9400, tipo: 'subcontrata', estado: 'pendiente' },
];

const tipoBadge: Record<string, string> = {
  material: 'bg-blue-500/20 text-blue-400',
  mano_obra: 'bg-green-500/20 text-green-400',
  subcontrata: 'bg-purple-500/20 text-purple-400',
  otros: 'bg-gray-500/20 text-gray-400',
};

const tipoLabel: Record<string, string> = {
  material: 'Material', mano_obra: 'Mano obra', subcontrata: 'Subcontrata', otros: 'Otros',
};

const fmtEur = (n: number) => n.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' €';

export default function CostesPage() {
  const [filtro, setFiltro] = useState<'todos' | 'pendiente' | 'pagado'>('todos');
  const visible = DEMO.filter(c => filtro === 'todos' || c.estado === filtro);
  const total = DEMO.reduce((s, c) => s + c.importe, 0);
  const pagado = DEMO.filter(c => c.estado === 'pagado').reduce((s, c) => s + c.importe, 0);
  const pendiente = DEMO.filter(c => c.estado === 'pendiente').reduce((s, c) => s + c.importe, 0);

  const porTipo = DEMO.reduce((acc, c) => {
    acc[c.tipo] = (acc[c.tipo] ?? 0) + c.importe;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Costes Reales</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control de gastos por obra</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black rounded-xl font-semibold text-sm hover:bg-orange-400 transition-colors">
          <Plus size={16} /> Registrar coste
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total costes</div>
          <div className="text-2xl font-bold">{fmtEur(total)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pagado</div>
          <div className="text-2xl font-bold text-green-400">{fmtEur(pagado)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 uppercase tracking-wide mb-1">
            <AlertTriangle size={12} className="text-orange-400" /> Pendiente pago
          </div>
          <div className="text-2xl font-bold text-orange-400">{fmtEur(pendiente)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Desglose por tipo</div>
          {Object.entries(porTipo).map(([tipo, imp]) => (
            <div key={tipo} className="mb-3 last:mb-0">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{tipoLabel[tipo]}</span>
                <span className="font-semibold">{fmtEur(imp)}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.round((imp / total) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Por obra</div>
          {['Construcción Chalet Goya', 'Reforma Oficinas'].map(obra => {
            const obraTotal = DEMO.filter(c => c.obra === obra).reduce((s, c) => s + c.importe, 0);
            return (
              <div key={obra} className="mb-3 last:mb-0">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 truncate">{obra.slice(0, 28)}</span>
                  <span className="font-semibold">{fmtEur(obraTotal)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.round((obraTotal / total) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        {(['todos', 'pagado', 'pendiente'] as const).map(f => (
          <button key={f} onClick={() => setFiltro(f)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${filtro === f ? 'bg-orange-500 text-black' : 'bg-white border border-gray-200 text-gray-500 hover:border-orange-300'}`}>
            {f === 'todos' ? 'Todos' : f === 'pagado' ? 'Pagados' : 'Pendientes'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Concepto</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Obra</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
              <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Importe</th>
              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(c => (
              <tr key={c.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                <td className="p-3 font-medium">{c.concepto}</td>
                <td className="p-3 text-gray-500 truncate max-w-[160px]">{c.obra.slice(0, 22)}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${tipoBadge[c.tipo]}`}>{tipoLabel[c.tipo]}</span></td>
                <td className="p-3 text-gray-400 text-xs">{c.fecha}</td>
                <td className="p-3 text-right font-bold">{fmtEur(c.importe)}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.estado === 'pagado' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {c.estado === 'pagado' ? 'Pagado' : 'Pendiente'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
